import { useState, useCallback, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import type { Asset } from "@/lib/api.ts";

type GalleryItem =
  | { type: "image"; url: string }
  | { type: "video"; url: string; posterUrl?: string };

/** Thumbnail for a video — renders a muted video element to show a real frame */
function VideoThumbnail({ url, alt }: { url: string; alt: string }) {
  return (
    <video
      src={url}
      muted
      preload="metadata"
      className="h-full w-full object-cover"
      aria-label={alt}
      // Seek slightly past 0 to get a real frame, not a black one
      onLoadedData={(e) => {
        const v = e.currentTarget;
        if (v.duration > 0.5) v.currentTime = 0.5;
      }}
    />
  );
}

interface Props {
  assets: Asset[];
  fallbackUrl?: string | null;
  productName: string;
  scrapedImages?: Array<{ url: string; alt?: string }>;
  scrapedVideos?: Array<{ url: string; poster?: string; caption?: string }>;
}

export function ImageGallery({ assets, fallbackUrl, productName, scrapedImages, scrapedVideos }: Props) {
  const imageAssets = assets.filter(
    (a) =>
      (a.mimeType?.startsWith("image/") || !a.mimeType) &&
      a.mimeType !== "image/gif" &&
      a.shotType !== "other" &&
      (a.qualityScore ?? 0) >= 3
  );

  // Collect image URLs — prefer local (downloaded), then Canto, scraped only as fallback
  const cantoUrls = imageAssets.slice(0, 7).map((a) => a.localPath || a.previewUrl || a.sourceUrl || "").filter(Boolean);
  const scrapedUrls = (scrapedImages ?? []).map((img) => img.url).filter(Boolean);

  // Build gallery items: images first, then videos
  const items: GalleryItem[] = [];
  const seenUrls = new Set<string>();

  // Canto image assets first (curated, tagged, quality-scored)
  for (const url of cantoUrls) {
    if (!seenUrls.has(url)) {
      seenUrls.add(url);
      items.push({ type: "image", url });
    }
  }

  // Scraped images as fallback when no Canto images are available
  if (cantoUrls.length === 0) {
    for (const url of scrapedUrls) {
      if (!seenUrls.has(url)) {
        seenUrls.add(url);
        items.push({ type: "image", url });
      }
    }
  }

  // Videos after images
  const videoList = scrapedVideos ?? [];
  for (let i = 0; i < videoList.length; i++) {
    const v = videoList[i];
    if (v.url && !seenUrls.has(v.url)) {
      seenUrls.add(v.url);
      items.push({ type: "video", url: v.url, posterUrl: v.poster });
    }
  }

  // Fallback URL
  if (items.length === 0 && fallbackUrl) {
    items.push({ type: "image", url: fallbackUrl });
  }

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const thumbContainerRef = useRef<HTMLDivElement>(null);
  const visibleItems = items.filter((item) => !failedUrls.has(item.url));
  const currentItem = visibleItems[selectedIndex];

  const handleImageFailed = useCallback((url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
    setSelectedIndex(0);
  }, []);

  const handleImageLoad = useCallback((url: string, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    // Filter out placeholder icons and tiny junk images
    if (img.naturalWidth < 200 && img.naturalHeight < 200) {
      handleImageFailed(url);
    }
  }, [handleImageFailed]);

  const prev = useCallback(() => {
    setSelectedIndex((i) => (i > 0 ? i - 1 : visibleItems.length - 1));
  }, [visibleItems.length]);

  const next = useCallback(() => {
    setSelectedIndex((i) => (i < visibleItems.length - 1 ? i + 1 : 0));
  }, [visibleItems.length]);

  // Scroll the active thumbnail into view when selectedIndex changes
  useEffect(() => {
    const container = thumbContainerRef.current;
    if (!container) return;
    const thumb = container.children[selectedIndex] as HTMLElement | undefined;
    if (thumb) {
      thumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    }
  }, [selectedIndex]);

  if (visibleItems.length === 0) {
    return (
      <div className="flex aspect-square items-center justify-center bg-bg-alt text-sm text-text-muted">
        No images available
      </div>
    );
  }

  return (
    <div className="flex flex-col-reverse gap-3 lg:flex-row lg:gap-4">
      {/* Thumbnail strip — below on mobile, left on desktop */}
      {visibleItems.length > 1 && (
        <div ref={thumbContainerRef} className="flex gap-2 overflow-x-auto lg:max-h-[600px] lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden">
          {visibleItems.map((item, i) => (
            <button
              key={item.url}
              onClick={() => setSelectedIndex(i)}
              className={`relative h-16 w-16 flex-shrink-0 overflow-hidden border-2 bg-bg-alt transition-colors lg:h-20 lg:w-20 ${
                i === selectedIndex
                  ? "border-heading"
                  : "border-transparent hover:border-border"
              }`}
            >
              {item.type === "video" ? (
                <div className="relative h-full w-full">
                  {item.posterUrl ? (
                    <img
                      src={item.posterUrl}
                      alt={`${productName} video ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <VideoThumbnail url={item.url} alt={`${productName} video ${i + 1}`} />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Play size={14} className="fill-white text-white drop-shadow" />
                  </div>
                </div>
              ) : (
                <img
                  src={item.url}
                  alt={`${productName} ${i + 1}`}
                  className="h-full w-full object-cover"
                  onLoad={(e) => handleImageLoad(item.url, e)}
                  onError={() => handleImageFailed(item.url)}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main display */}
      <div className="group relative flex-1">
        <div className="flex aspect-square items-center justify-center bg-bg-alt">
          {currentItem?.type === "video" ? (
            <iframe
              src={currentItem.url}
              title={productName}
              className="h-full w-full"
              allow="autoplay; fullscreen"
              allowFullScreen
            />
          ) : currentItem ? (
            <img
              src={currentItem.url}
              alt={productName}
              className="h-full w-full object-contain p-6"
              onLoad={(e) => handleImageLoad(currentItem.url, e)}
              onError={() => handleImageFailed(currentItem.url)}
            />
          ) : null}
        </div>

        {/* Nav arrows */}
        {visibleItems.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 opacity-0 transition-opacity hover:bg-white group-hover:opacity-100"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 p-2 opacity-0 transition-opacity hover:bg-white group-hover:opacity-100"
              aria-label="Next image"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}

        {/* Dot indicators (mobile) */}
        {visibleItems.length > 1 && (
          <div className="mt-3 flex justify-center gap-1.5 lg:hidden">
            {visibleItems.map((_, i) => (
              <button
                key={i}
                onClick={() => setSelectedIndex(i)}
                className={`h-2 w-2 rounded-full transition-colors ${
                  i === selectedIndex ? "bg-heading" : "bg-border"
                }`}
                aria-label={`Item ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
